import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { Table } from '../../components/Table';
import { useSession } from '../../lib/useSession';
import { describeApiError } from '../qrs/errors';
import { itemsOf, useApiQuery } from '../qrs/hooks';
import { permissionsFor } from '../qrs/permissions';
import { formatDate, formatNumber } from '../qrs/states';
import type { Lot } from '../qrs/types';
import { useOrgApi } from '../qrs/useOrgApi';
import { CreateLotModal } from './CreateLotModal';
import { lotDistribution, lotStatus } from './lotUtils';



export function LotsPage() {
  const client = useOrgApi();
  const navigate = useNavigate();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const [creating, setCreating] = useState(false);
  const lots = useApiQuery((signal) => client.get<{ items: Lot[] } | Lot[]>('/v1/lots', { signal }).then(itemsOf), [client]);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Lotes</h1>
          <p className="text-sm text-ink-muted">Cada lote reserva códigos para imprimir, repartir y activar en terreno.</p>
        </div>
        {perms.manageLots && <Button onClick={() => setCreating(true)}>Pedir lote</Button>}
      </div>
      {lots.error ? (
        <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={lots.reload}>Reintentar</Button>}>
          {describeApiError(lots.error, 'No pudimos cargar los lotes.')}
        </Banner>
      ) : !lots.loading && (lots.data ?? []).length === 0 ? (
        <EmptyState
          title="Aún no pides lotes"
          description="Un lote reserva entre 1 y 5.000 códigos. Después marcas la imprenta, repartes a tus vendedores y ellos activan en terreno."
          action={perms.manageLots ? <Button onClick={() => setCreating(true)}>Pide tu primer lote</Button> : undefined}
        />
      ) : (
        <Table<Lot>
          caption="Lotes de la organización"
          loading={lots.loading}
          rows={lots.data ?? []}
          rowKey={(l) => l.id}
          columns={[
            {
              key: 'id',
              header: 'Lote',
              render: (l) => (
                <span>
                  <span className="font-mono">{l.id}</span>
                  {l.name && <span className="block text-xs text-ink-muted">{l.name}</span>}
                </span>
              ),
            },
            { key: 'created', header: 'Creado', render: (l) => formatDate(l.createdAt) },
            { key: 'qty', header: 'Cantidad', align: 'right', render: (l) => <span className="tabular-nums">{formatNumber(l.quantity)}</span> },
            {
              key: 'status',
              header: 'Estado del lote',
              render: (l) => {
                const s = lotStatus(l);
                return <Chip tone={s.tone}>{s.label}</Chip>;
              },
            },
            { key: 'dist', header: 'Distribución', render: (l) => <span className="text-xs text-gray-700">{lotDistribution(l)}</span> },
            {
              key: 'go',
              header: '',
              align: 'right',
              render: (l) => (
                <Link to={`/consola/lotes/${encodeURIComponent(l.id)}`} className="font-medium text-green underline hover:text-ink">
                  Ver lote
                </Link>
              ),
            },
          ]}
        />
      )}
      <CreateLotModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(lot) => {
          setCreating(false);
          navigate(`/consola/lotes/${encodeURIComponent(lot.id)}`);
        }}
      />
    </section>
  );
}
